import { formatDate } from '@mg/web-components/utils/date'
import { FormatDateType } from '@mg/web-components/utils/date/types'
import moment from 'moment'
import { isTomorrow, isToday } from '~/utils/date'
import { GQLDeliveryItem } from '~/graphql/__generated__/interfaces'
import { DetailModel } from '~/utils/domain/Detail'
import { Shops } from '~/config/shops'

export const UNAVAILABLE_STATUSES: { [key: string]: string | undefined } = {
  N1: 'avail_sold_out',
  N3: 'avail_not_sold_separately',
}

export enum AvailabilityKeys {
  AvailableInStockMall = 'avail-stock-mall',
  AvailableSupplier = 'avail-supplier',
  AvailableStockMall = 'avail-stock-external',
  AvailableStockCentral = 'avail-stock-central',
  AvailableStockPartner = 'avail-stock-partner',
  AvailablePartnerOnDemand = 'avail-partner-on_demand',
  AvailableDigitalContent = 'avail-digital-content',
  AvailableProblematicSuplier = 'avail-problematic_supplier',
}

const DELIVERY_OUTSIDE = 'outside'
const ON_DEMAND_MP_DAYS = 7

export default class Availability {
  product: DetailModel
  translations: Record<string, any>
  translateWithParams: Function
  shopId: string
  availabilityTooltipTranslationKeys: {
    [key in AvailabilityKeys]: { header: string; description: String }
  }

  constructor(
    product: DetailModel,
    translations: Record<string, any>,
    translateWithParams: Function,
    shopId: string
  ) {
    this.product = product
    this.translations = translations
    this.translateWithParams = translateWithParams
    this.shopId = shopId
    this.setAvailabilityTooltipTranslations(translations)
  }

  get availabilityKey() {
    return this.product.availability?.key || ''
  }

  get snakeCasedAvailabilityKey() {
    const str = this.product.availability?.key!
    return str.split('-').join('_') || ''
  }

  get status() {
    return this.product.availability?.status!
  }

  get isAvailable() {
    return !this.isNotSoldSeparately && this.product.isAvailable
  }

  get mallAvailabilityCount() {
    return this.product.availability?.countMall!
  }

  get outletAvailabilityCount() {
    return this.product.availability?.countOutlets!
  }

  get supplierAvailabilityCount() {
    return this.product.availability?.countSupplier!
  }

  get externalAvailabilityCount() {
    return this.product.availability?.countExternal!
  }

  get onlyCashCarry() {
    return (
      this.outletAvailabilityCount > 0 &&
      this.mallAvailabilityCount === 0 &&
      this.externalAvailabilityCount === 0 &&
      this.supplierAvailabilityCount === 0
    )
  }

  get hasCashCarry() {
    return this.outletAvailabilityCount > 0
  }

  get mpAvailabilityOnDemand() {
    return (
      this.product.variantMarketplaceData?.deliveryDelay! > ON_DEMAND_MP_DAYS
    )
  }

  get isMarketPlace() {
    return this.product.variantMarketplaceData?.isMarketplace
  }

  get isDigitalContent() {
    return this.availabilityKey === 'avail-digital-content'
  }

  get supplierAvailability() {
    return this.availabilityKey === 'avail-supplier'
  }

  get isSoldOut() {
    return this.availabilityKey === 'avail-sold-out'
  }

  get isAvailableSoon() {
    return this.availabilityKey === 'avail-soon'
  }

  get isStockPartner() {
    return this.availabilityKey === 'avail-stock-partner'
  }

  get isNotSoldSeparately() {
    return this.availabilityKey === 'avail-not-sold-separately'
  }

  get matchUnavailableStatus() {
    return Object.keys(UNAVAILABLE_STATUSES).includes(this.status)
  }

  getTranslationForAvailableProduct() {
    if (this.isMarketPlace && this.mpAvailabilityOnDemand) {
      return this.translations.txt_avail_marketplace_on_demand
    }

    if (this.isDigitalContent) {
      return this.translations.avail_digital_content
    }

    if (this.isStockPartner) {
      return this.mallAvailabilityTranslation
    }

    // TODO: will be done in separate ticket (no data on urban)
    // if (this.isVoucher) {
    //   return this.translations.avail_stock_mall
    // }

    if (this.supplierAvailability) {
      return this.translations.avail_supplier
    }

    if (this.onlyCashCarry) {
      if (!this.availabilityKey) {
        return ' '
      }

      if (this.isSlovenia) {
        return `
        ${this.translations.avail_on_stock_in_shop}
         `
      }

      return `${this.translations[this.snakeCasedAvailabilityKey]} ${
        this.cashCarryAvailabilityTranslation
      }`
    }

    return this.mallAvailabilityTranslation
  }

  get mallAvailabilityTranslation() {
    if (!this.availabilityKey) {
      return ' '
    }

    const translatedAvailabilityKey = this.translations[
      this.snakeCasedAvailabilityKey
    ]
    const count = this.getAvailabilityCountTranslationKey(
      this.mallAvailabilityCount
    )

    const result = this.translateWithParams(count, [this.mallAvailabilityCount])

    return `${translatedAvailabilityKey} ${result}`
  }

  get cashCarryAvailabilityTranslation() {
    if (this.outletAvailabilityCount > 1) {
      return this.translateWithParams(
        String(this.translations.txt_avail_total_cash_carry_many),
        [this.outletAvailabilityCount]
      )
    }

    return this.translations.txt_avail_total_cash_carry_one
  }

  getAvailabilityCountTranslationKey(count: number) {
    if (count > 5) {
      return this.translations.avail_five_more as string
    }

    const translations: { [key: number]: string } = {
      0: '',
      1: this.translations.avail_one as string,
      2: this.translations.avail_two as string,
      3: this.translations.avail_three as string,
      4: this.translations.avail_four as string,
      5: this.translations.avail_five as string,
    }

    return translations[count]
  }

  getTranslationForUnavailableProduct() {
    if (this.matchUnavailableStatus) {
      return this.translations[UNAVAILABLE_STATUSES[this.status]!]
    }

    if (this.isAvailableSoon) {
      return this.translations.avail_soon
    }

    if (this.isSoldOut) {
      return this.translations.avail_sold_out
    }

    return this.translations.avail_unavailable
  }

  get availabilityUnknown() {
    return (
      !this.isAvailable &&
      !this.matchUnavailableStatus &&
      !this.isAvailableSoon &&
      !this.isSoldOut
    )
  }

  get availabilityStatus() {
    if (this.isAvailable) {
      return this.getTranslationForAvailableProduct()
    } else {
      return this.getTranslationForUnavailableProduct()
    }
  }

  get availabilityStatusWithDate() {
    const minHomeDelivery = this.getMinDate(this.homeDelivery)
    const minPUPDelivery = this.getMinDate(this.pickupPoints, true)
    const deliveryDates = [minHomeDelivery, minPUPDelivery]
      .filter(Boolean)
      .map((date) => Number(date))
    const closestDeliveryDate = new Date(Math.min(...deliveryDates))
    const availabilityStatus = this.translations[this.snakeCasedAvailabilityKey]

    if (!closestDeliveryDate) {
      return availabilityStatus
    } else {
      const shortDate = String(
        this.formatedDateWithTodayTomorrow(closestDeliveryDate)
      ).toLowerCase()

      return `${availabilityStatus} – ${this.translations.txt_date_delivery} ${shortDate}`
    }
  }

  get homeDeliveryDateRange() {
    if (!this.product.estimatedDeliveries?.home[0]) {
      return ''
    }

    const {
      deliveryFrom,
      deliveryTo,
    } = this.product.estimatedDeliveries?.home[0]

    return `${formatDate(deliveryFrom!, FormatDateType.long)} -
    ${formatDate(deliveryTo!, FormatDateType.long)}`
  }

  get closestCaCDeliveryDate() {
    if (!this.cashCarry.length) {
      return null
    }

    return this.getMinDate(this.cashCarry, true)
  }

  get closestPickupPointDeliveryDate() {
    if (!this.pickupPoints.length) {
      return null
    }

    return this.getMinDate(this.pickupPoints, true)
  }

  get closestHomeDeliveryDate() {
    if (this.isMarketPlace) {
      return this.homeDeliveryDateRange
    }

    const date = this.getMinDate(this.homeDelivery)

    if (!date) {
      return null
    }

    if (isToday(date)) {
      return this.translations.txt_avail_today
    }

    if (isTomorrow(date)) {
      return this.translations.txt_avail_tomorrow
    }

    return formatDate(date)
  }

  get closestCarrierOtherDate() {
    if (this.isMarketPlace) {
      return this.homeDeliveryDateRange
    }

    const deliveriesOutside = this.homeDelivery.filter((deliveryItem) =>
      deliveryItem?.locality?.includes(DELIVERY_OUTSIDE)
    )

    const date = this.getMinDate(deliveriesOutside)

    if (!date) {
      return null
    }

    if (isToday(date)) {
      return this.translations.txt_avail_today
    }

    if (isTomorrow(date)) {
      return this.translations.txt_avail_tomorrow
    }

    return formatDate(date)
  }

  get highlightCarrierDeliveryDate() {
    const date = this.getMinDate(this.homeDelivery)

    if (!date) {
      return null
    }

    return (isToday(date) || isTomorrow(date)) && !this.isMarketPlace
  }

  get cacDeliveryDate() {
    if (this.closestCaCDeliveryDate === null) {
      return ''
    }

    if (isToday(this.closestCaCDeliveryDate)) {
      return this.translations.txt_avail_immediately
    }

    if (isTomorrow(this.closestCaCDeliveryDate)) {
      return this.translations.txt_avail_tomorrow
    }
  }

  get pickUpPointDeliveryDate() {
    if (this.closestPickupPointDeliveryDate === null) {
      return ''
    }

    if (isToday(this.closestPickupPointDeliveryDate)) {
      return this.translations.txt_avail_today
    }

    if (isTomorrow(this.closestPickupPointDeliveryDate)) {
      return this.translations.txt_avail_tomorrow
    }

    return formatDate(this.closestPickupPointDeliveryDate)
  }

  get outletDeliveryDate() {
    if (this.hasCashCarry && this.cacDeliveryDate) {
      return this.cacDeliveryDate
    }

    return this.pickUpPointDeliveryDate
  }

  get highlightOutletDeliveryDate() {
    return (
      this.hasCashCarry ||
      isToday(this.closestPickupPointDeliveryDate!) ||
      isTomorrow(this.closestPickupPointDeliveryDate!)
    )
  }

  get deliveryTypeOutlet() {
    if (this.hasCashCarry) {
      return this.cashCarryAvailabilityTranslation
    }

    return this.translations.avail_for_outlets
  }

  get deliveryTypeCarrier() {
    return this.translations.avail_for_carrier
  }

  get deliveryTypeCarrierMarketplace() {
    return this.translations.txt_detail_availability_carrier_marketplace
  }

  get deliveryTypeCarrierOther() {
    return this.translations.txt_detail_availability_carrier_other
  }

  get deliveryTypeEconomy() {
    return this.translations.txt_detail_availability_carrier_standard
  }

  get homeDelivery() {
    return (
      (this.product.estimatedDeliveries?.home as GQLDeliveryItem[]) ||
      ([] as GQLDeliveryItem[])
    )
  }

  get pickupPoints() {
    return (
      (this.product.estimatedDeliveries?.pup as GQLDeliveryItem[]) ||
      ([] as GQLDeliveryItem[])
    )
  }

  get cashCarry() {
    return (
      (this.product.estimatedDeliveries?.cac as GQLDeliveryItem[]) ||
      ([] as GQLDeliveryItem[])
    )
  }

  getMinDate(deliveryDates: GQLDeliveryItem[], isPickUpPoint: boolean = false) {
    if (!deliveryDates.length) {
      return null
    }

    const useOutsideOfCapitalDeliveries = deliveryDates.some((deliveryItem) =>
      deliveryItem?.locality?.includes(DELIVERY_OUTSIDE)
    )

    const useCapitalDeliveries = deliveryDates.some(
      (deliveryItem) => !deliveryItem?.locality?.includes(DELIVERY_OUTSIDE)
    )

    const deliveriesOutside = deliveryDates.filter((deliveryItem) =>
      deliveryItem?.locality?.includes(DELIVERY_OUTSIDE)
    )

    const deliveriesToCapital = deliveryDates.filter(
      (deliveryItem) => !deliveryItem?.locality?.includes(DELIVERY_OUTSIDE)
    )

    const prefferedDeliveryDates = isPickUpPoint
      ? useCapitalDeliveries
        ? deliveriesToCapital
        : deliveriesOutside
      : useOutsideOfCapitalDeliveries
      ? deliveriesOutside
      : deliveriesToCapital

    const deliveryDateCountry = this.isCroatia
      ? deliveryDates
      : prefferedDeliveryDates

    const dates = deliveryDateCountry.reduce((acc, deliveryItem) => {
      const { deliveryFrom } = deliveryItem!
      if (deliveryFrom) {
        const deliveryDate = moment(deliveryFrom).toDate()
        return [...acc, deliveryDate]
      }
      return acc
    }, [] as Date[])
    const datesAsNumber = dates.map((date) => Number(date))

    if (!datesAsNumber.length) {
      return null
    }

    return new Date(Math.min(...datesAsNumber))
  }

  formatedDateWithTodayTomorrow(date: string | Date) {
    if (!date) {
      return ''
    }

    if (isToday(date)) {
      return this.translations.txt_avail_today
    }

    if (isTomorrow(date)) {
      return this.translations.txt_avail_tomorrow
    }

    return formatDate(date)
  }

  get tooltipTranslation() {
    const t = this.availabilityTooltipTranslationKeys[
      this.availabilityKey as AvailabilityKeys
    ]
    if (!t) {
      return { description: '', header: '' }
    }
    return t
  }

  get isCroatia() {
    return this.shopId === Shops.HR10MA
  }

  get isSlovenia() {
    return this.shopId === Shops.SI10SI
  }

  setAvailabilityTooltipTranslations(translations: Record<string, any>) {
    this.availabilityTooltipTranslationKeys = {
      [AvailabilityKeys.AvailableInStockMall]: {
        description: !this.onlyCashCarry
          ? translations.txt_detail_availability
          : '',
        header: translations.hdg_detail_availability,
      },
      [AvailabilityKeys.AvailableSupplier]: {
        description: translations.txt_detail_availability_supplier,
        header: translations.hdg_detail_availability_supplier,
      },
      [AvailabilityKeys.AvailableStockMall]: {
        description: translations.txt_detail_availability_external,
        header: translations.hdg_detail_availability_external,
      },
      [AvailabilityKeys.AvailableStockCentral]: {
        description: translations.txt_detail_availability_central,
        header: translations.hdg_detail_availability_central,
      },
      [AvailabilityKeys.AvailableStockPartner]: {
        description: '',
        header: translations.hdg_marketplace_availability,
      },
      [AvailabilityKeys.AvailablePartnerOnDemand]: {
        description: translations.txt_marketplace_availability_on_demand,
        header: translations.txt_avail_marketplace_on_demand,
      },
      [AvailabilityKeys.AvailableDigitalContent]: {
        description: translations.txt_detail_availability_digital,
        header: translations.hdg_detail_availability,
      },
      [AvailabilityKeys.AvailableProblematicSuplier]: {
        description: translations.txt_detail_availability_problematic_supplier,
        header: translations.hdg_detail_availability_problematic_supplier,
      },
    }
  }
}

export const snakeCasedAvailabilityKey = (availabilityKey: string) => {
  if (!availabilityKey) {
    return ''
  }

  return availabilityKey.split('-').join('_')
}
